import { Box, Stack, Typography, Card, CardActionArea, CardContent, Chip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { GameSession } from '../../types'

interface RecentActivityListProps {
  sessions: GameSession[]
}

/** Format `playedAt` ISO string as a human-friendly relative-time label. */
function relTime(iso: string, t: (k: string, v?: Record<string, unknown>) => string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diffMin = Math.round((now - then) / 60000)
  if (diffMin < 1) return t('games.recentTimeAgo.justNow')
  if (diffMin < 60) return t('games.recentTimeAgo.minutesAgo', { count: diffMin })
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return t('games.recentTimeAgo.hoursAgo', { count: diffHr })
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay === 1) return t('games.recentTimeAgo.yesterday')
  if (diffDay < 7) return t('games.recentTimeAgo.daysAgo', { count: diffDay })
  return new Date(iso).toLocaleDateString()
}

function gameTypeToKey(gt: string): string {
  // 'multiple-choice' → 'multipleChoice' to match the existing
  // games.<type>Title i18n keys.
  return gt.split('-').map((part, i) => (i === 0 ? part : part[0].toUpperCase() + part.slice(1))).join('')
}

export function RecentActivityList({ sessions }: RecentActivityListProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t('games.recentActivity')}
      </Typography>
      {sessions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('games.recentEmpty')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {sessions.map((s) => (
            <Card key={s.sessionId} variant="outlined">
              <CardActionArea onClick={() => navigate(`/games/${s.gameType}`)}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t(`games.${gameTypeToKey(s.gameType)}Title`)}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center' }}>
                      <Chip label={s.source} size="small" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {t('games.recentScore', { correct: s.correctAnswers, total: s.totalQuestions })}
                      </Typography>
                    </Stack>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {relTime(s.playedAt, t)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  )
}
